import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ENV } from '@shared/config/env'
import { classifyAuthError, parse429 } from '@shared/api/httpErrors'
import { getProfile, updateProfileName, useInvalidateProfile } from '@entities/user'
import { useAppStore } from '@/store/useAppStore'
import { sendCode, resendCode, verifyCode } from '../api/webAuthApi'
import { useAuthFlowStore } from './useAuthFlowStore'
import { saveNextResend, formatResendWaitMessage } from './useResendTimer'

interface UseAuthFlowParams {
  redirectTo: string
}

interface UseAuthFlowReturn {
  state: ReturnType<typeof useAuthFlowStore.getState>
  submitting: boolean
  /** Идёт ревалидация персистнутого шага через getProfile — рендерить ничего не нужно */
  revalidating: boolean
  shakeSignal: number
  debugCode: string | null

  handleEmailSubmit: (email: string, allowRegistration?: boolean) => Promise<void>
  handleResend: () => Promise<void>
  handleVerify: (code: string) => Promise<void>
  handleNameSubmit: (name: string) => Promise<void>
  handleTelegramLinked: () => Promise<void>
  handleSkipTelegram: () => void
  changeEmail: () => void
}

/**
 * Инкапсулирует бизнес-логику флоу веб-авторизации.
 * @param params.redirectTo - URL для редиректа после успеха
 * @returns Обработчики шагов и состояние флоу
 */
export const useAuthFlow = ({ redirectTo }: UseAuthFlowParams): UseAuthFlowReturn => {
  const navigate = useNavigate()
  const store = useAuthFlowStore()
  const {
    email,
    setStep, setEmail, setCooldown, setError, setAttemptsLeft, setLocked,
    setNeedsRegistration, setDebugCode, reset
  } = store
  const setProfile = useAppStore((s) => s.setProfile)
  const setAuthStatus = useAppStore((s) => s.setAuthStatus)
  const invalidateProfile = useInvalidateProfile()
  const [submitting, setSubmitting] = useState(false)
  const [shakeSignal, setShakeSignal] = useState(0)

  // Персист (sessionStorage) хранит step+email. Шаги code/name валидны
  // только при живой сессии на бэке — после reload её надо перепроверить,
  // иначе юзер застревает на этих шагах без сессии. Ревалидируем ровно
  // один раз при маунте по гидрированному шагу.
  //
  // Шаг offer-telegram СОЗНАТЕЛЬНО исключён: он достижим только сразу после
  // успешного verify (сессия заведомо живая), а ремаунт при openLink TG
  // (новая вкладка / возврат фокуса / StrictMode) запускал бы getProfile и
  // редиректил в профиль до подтверждения привязки. С этого шага уходим
  // только по onLinked (привязка подтверждена) или onSkip (явный пропуск).
  const hydratedStep = useRef(useAuthFlowStore.getState().step).current
  const needsRevalidation = hydratedStep === 'code' || hydratedStep === 'name'
  const [revalidating, setRevalidating] = useState(needsRevalidation)

  useEffect(() => {
    if (!needsRevalidation) return
    let cancelled = false
    void (async () => {
      try {
        const profile = await getProfile()
        if (cancelled) return
        if (hydratedStep === 'name') {
          // Юзер уже дошёл до шага имени (прошёл/пропустил предложение TG) —
          // не откатываем назад; если имя уже есть — внутрь.
          if (profile.name === '') return
          setProfile(profile)
          setAuthStatus('success')
          await invalidateProfile()
          reset()
          navigate(redirectTo, { replace: true })
        } else if (!profile.telegram) {
          // Ремаунт на шаге code, TG не привязан — предлагаем привязку
          // (то же правило, что в handleVerify).
          setStep('offer-telegram')
        } else if (profile.name === '') {
          setStep('name')
        } else {
          setProfile(profile)
          setAuthStatus('success')
          await invalidateProfile()
          reset()
          navigate(redirectTo, { replace: true })
        }
      } catch {
        // Нет валидной сессии — начинаем флоу заново с email
        if (!cancelled) reset()
      } finally {
        if (!cancelled) setRevalidating(false)
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const parseAttemptsLeft = (err: unknown): number | null => {
    const detail = (err as { response?: { data?: { detail?: unknown } } } | undefined)?.response?.data?.detail
    if (typeof detail !== 'string') return null
    const match = /попыток:\s*(\d+)/i.exec(detail)
    return match ? Number(match[1]) : null
  }

  const handleEmailSubmit = async (value: string, allowRegistration = false) => {
    setSubmitting(true); setError(null)
    try {
      const res = await sendCode(value, allowRegistration)
      setEmail(value)
      setCooldown(res.next_resend, res.expired_at)
      saveNextResend(res.next_resend)
      if (ENV.IS_DEV && res.code) setDebugCode(res.code)
      setNeedsRegistration(false)
      setStep('code')
    } catch (err) {
      const cooldown = parse429(err)
      if (cooldown) {
        setEmail(value)
        setCooldown(cooldown.next_resend, cooldown.expired_at)
        saveNextResend(cooldown.next_resend)
        if (ENV.IS_DEV && cooldown.code) setDebugCode(cooldown.code)
        setNeedsRegistration(false)
        setStep('code')
        setError(formatResendWaitMessage(cooldown.next_resend) ?? cooldown.message)
        return
      }
      const kind = classifyAuthError(err)
      if (kind === 'already_authenticated') {
        navigate(redirectTo, { replace: true })
        return
      }
      if (kind === 'registration_required') {
        // Юзера нет — переключаем форму в режим регистрации, остаёмся на шаге email.
        setEmail(value)
        setNeedsRegistration(true)
        return
      }
      setError(kind === 'rate_limit'
        ? 'Слишком много запросов. Попробуйте позже.'
        : 'Не удалось отправить код')
    } finally {
      setSubmitting(false)
    }
  }

  const handleResend = async () => {
    if (!email) return
    setSubmitting(true); setError(null)
    setLocked(false); setAttemptsLeft(null)
    try {
      const res = await resendCode(email)
      setCooldown(res.next_resend, res.expired_at)
      saveNextResend(res.next_resend)
      if (ENV.IS_DEV && res.code) setDebugCode(res.code)
    } catch (err) {
      const cooldown = parse429(err)
      if (cooldown) {
        setCooldown(cooldown.next_resend, cooldown.expired_at)
        saveNextResend(cooldown.next_resend)
        setError(formatResendWaitMessage(cooldown.next_resend) ?? cooldown.message)
        return
      }
      const kind = classifyAuthError(err)
      if (kind === 'code_not_found') {
        const res = await sendCode(email)
        setCooldown(res.next_resend, res.expired_at)
        saveNextResend(res.next_resend)
        if (ENV.IS_DEV && res.code) setDebugCode(res.code)
      } else {
        setError('Не удалось отправить код')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleVerify = async (code: string) => {
    if (!email) return
    setSubmitting(true); setError(null)

    try {
      await verifyCode(email, code)
    } catch (err) {
      const kind = classifyAuthError(err)
      switch (kind) {
        case 'code_invalid':
          setShakeSignal((x) => x + 1)
          setAttemptsLeft(parseAttemptsLeft(err))
          setError('Неверный код.')
          break
        case 'code_expired':
          setError('Срок действия кода истёк. Запросите новый.')
          setLocked(true)
          break
        case 'code_not_found':
          setError('Код не найден. Запросите новый.')
          setLocked(true)
          break
        case 'max_attempts':
          setLocked(true)
          setError('Код недействителен. Запросите новый код.')
          break
        case 'rate_limit':
          setLocked(true)
          setError('Слишком много попыток. Попробуйте позже.')
          break
        case 'already_authenticated':
          navigate(redirectTo, { replace: true })
          return
        default:
          setError('Не удалось проверить код')
      }
      setSubmitting(false)
      return
    }

    saveNextResend(null)
    let profile = null as Awaited<ReturnType<typeof getProfile>> | null
    try {
      profile = await getProfile()
    } catch (err) {
      const anyErr = err as { response?: { status?: number } }
      setError(`Код принят, но /user/profile вернул ${anyErr?.response?.status ?? '?'}`)
      setSubmitting(false)
      return
    }

    if (!profile.telegram) {
      // Telegram не привязан: предлагаем привязать его сейчас, чтобы
      // существующая TG-подписка/данные перенеслись на этот email.
      // Не зависит от имени — шаг показывается всегда при отсутствии TG.
      setStep('offer-telegram')
    } else if (profile.name === '') {
      setStep('name')
    } else {
      setProfile(profile)
      setAuthStatus('success')
      await invalidateProfile()
      reset()
      navigate(redirectTo, { replace: true })
    }
    setSubmitting(false)
  }

  const handleNameSubmit = async (name: string) => {
    setSubmitting(true); setError(null)
    try {
      const profile = await updateProfileName(name)
      setProfile(profile)
      setAuthStatus('success')
      await invalidateProfile()
      reset()
      navigate(redirectTo, { replace: true })
    } catch {
      setError('Не удалось сохранить имя')
    } finally {
      setSubmitting(false)
    }
  }

  const handleTelegramLinked = async () => {
    setError(null)
    // После привязки бэкенд мёржит TG-аккаунт (имя/подписку) в текущий.
    // Если имя пришло из Telegram — шаг имени не нужен, сразу внутрь.
    let profile = null as Awaited<ReturnType<typeof getProfile>> | null
    try {
      profile = await getProfile()
    } catch {
      setStep('name')
      return
    }
    if (profile.name === '') {
      setStep('name')
      return
    }
    setProfile(profile)
    setAuthStatus('success')
    await invalidateProfile()
    reset()
    navigate(redirectTo, { replace: true })
  }

  const handleSkipTelegram = () => {
    setError(null)
    setStep('name')
  }

  const changeEmail = () => {
    setError(null)
    setLocked(false)
    setAttemptsLeft(null)
    setNeedsRegistration(false)
    setStep('email')
  }

  return {
    state: store,
    submitting,
    revalidating,
    shakeSignal,
    debugCode: store.debugCode,
    handleEmailSubmit,
    handleResend,
    handleVerify,
    handleNameSubmit,
    handleTelegramLinked,
    handleSkipTelegram,
    changeEmail
  }
}
